import { createContext, useContext, useState, useEffect, ReactNode } from "react";
import { useColorMode, useColorModeValue } from "@chakra-ui/react";

// Theme context type
interface ThemeContextType {
  theme: string;
  setTheme: React.Dispatch<React.SetStateAction<string>>;
  toggleTheme: () => void;
}

// Create the Theme Context with default values
const ThemeContext = createContext<ThemeContextType>({
  theme: "light",
  setTheme: () => {},
  toggleTheme: () => {},
});

interface ThemeProviderProps {
  children: ReactNode;
}

export function ThemeProvider({ children }: ThemeProviderProps) {
  const { colorMode, toggleColorMode, setColorMode } = useColorMode();
  const [theme, setTheme] = useState<string>(colorMode);

  useEffect(() => {
    // Sync our theme state with Chakra's color mode
    setTheme(colorMode);
    // Also set data-theme attribute for CSS variables
    document.documentElement.setAttribute('data-theme', colorMode);
    document.body.setAttribute('data-theme', colorMode);
  }, [colorMode]);

  const toggleTheme = () => {
    toggleColorMode();
  };

  return (
    <ThemeContext.Provider value={{ theme, setTheme, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useCustomColorModeValue<T>(lightValue: T, darkValue: T): T {
  return useColorModeValue(lightValue, darkValue);
}

export function useThemeColor(lightColor: string, darkColor: string): string {
  return useColorModeValue(lightColor, darkColor);
}

export function useThemeToggle() {
  const { theme, toggleTheme } = useContext(ThemeContext);
  return { theme, toggleTheme };
}
